import {gql} from '@apollo/client';

import {getApolloClient} from 'graphql-client/ApolloClient';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {MAX_RECENT_WORKLISTS_TO_DISPLAY} from 'worklists/WorklistConstants';

import {RecentWorklistIdsQuery} from './__generated/RecentWorklistIdsQuery';
import {RecentWorklistsQuery, RecentWorklistsQueryVariables} from './__generated/RecentWorklistsQuery';

// Never executed on server; used to update Apollo cache
export const RECENT_WORKLIST_IDS_QUERY = gql`
  query RecentWorklistIdsQuery($limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: WORKLISTS) {
    recentWorklists(limitOffsetInput: $limitOffsetInput) {
      worklists {
        ... on WorklistNode {
          id
        }
      }
    }
  }
`;

export const RECENT_WORKLISTS_QUERY = gql`
  query RecentWorklistsQuery($limitOffsetInput: LimitOffsetInput!) @skipAudit @engComponent(name: WORKLISTS) {
    recentWorklists(limitOffsetInput: $limitOffsetInput) {
      worklists {
        ... on WorklistNode {
          ...WorklistSummaryGraphQL
          items {
            __typename
          }
        }
      }
    }
  }
  ${getFragmentWrapper('WorklistSummary').fragment}
`;

export const RECENT_WORKLISTS_QUERY_VARIABLES: RecentWorklistsQueryVariables = {
  limitOffsetInput: {
    limit: MAX_RECENT_WORKLISTS_TO_DISPLAY,
    offset: 0,
  },
};

export interface RecentWorklistsQueryResult {
  queryResultType: QueryResultType;
  recentWorklists: RecentWorklistsQuery['recentWorklists']['worklists'] | null;
}

export function updateRecentWorklistIdsCache(recentWorklistIds: Array<string>): void {
  const maxRecentWorklistIds = recentWorklistIds.slice(0, MAX_RECENT_WORKLISTS_TO_DISPLAY);
  const response: RecentWorklistIdsQuery = {
    recentWorklists: {
      worklists: maxRecentWorklistIds.map((id) => ({id, __typename: 'WorklistNode'})),
      __typename: 'RecentWorklistListingNode',
    },
  };

  getApolloClient().writeQuery({
    query: RECENT_WORKLIST_IDS_QUERY,
    variables: RECENT_WORKLISTS_QUERY_VARIABLES,
    data: response,
  });
}

export async function fetchRecentWorklistsQuery(): Promise<void> {
  await doQuerySafe<RecentWorklistsQuery, RecentWorklistsQueryVariables>(
    {query: RECENT_WORKLISTS_QUERY},
    {variables: RECENT_WORKLISTS_QUERY_VARIABLES}
  );
}

export default function useRecentWorklistsQuery(): RecentWorklistsQueryResult {
  const result = useBenchlingQuery<RecentWorklistsQuery, RecentWorklistsQueryVariables>(
    RECENT_WORKLISTS_QUERY,
    {variables: RECENT_WORKLISTS_QUERY_VARIABLES}
  );
  const worklists = result.type === QueryResultType.SUCCESS ? result.data.recentWorklists.worklists : null;
  return {queryResultType: result.type, recentWorklists: worklists};
}
